import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { Form, Popover } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import apiClient from '../../utils/apiService';
import './index.css';

// Fields that support field index autocomplete
const AUTOCOMPLETE_FIELDS = {
  port_of_loading: { fieldName: 'port_of_loading', needPic: false },
  port_of_discharge: { fieldName: 'port_of_discharge', needPic: false },
  place_of_delivery: { fieldName: 'place_of_delivery', needPic: false },
  freight_location: { fieldName: 'freight_location', needPic: false },
  freight_tel: { fieldName: 'freight_tel', needPic: false },
  it_number: { fieldName: 'it_number', needPic: false },
  consignee_email: { fieldName: 'consignee_email', needPic: true },
  notify_email: { fieldName: 'notify_email', needPic: true },
};

const DEBOUNCE_MS = 200;

const SuggestionPanel = React.memo(function SuggestionPanel({
  visible,
  anchorRect,
  activeFieldName,
  pic,
  loading,
  results,
  onSelect,
  onClose,
}) {
  if (!visible || !anchorRect) return null;

  const fieldLabel = activeFieldName
    ? activeFieldName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

  const content = (
    <div
      className="field-index-panel"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="field-index-panel-header">
        <span className="field-index-panel-title">
          {fieldLabel}
          {pic && <span className="field-index-panel-pic">({pic})</span>}
          {results.length > 0 && !loading && (
            <span className="field-index-panel-count">({results.length} items)</span>
          )}
        </span>
        <CloseOutlined className="field-index-panel-close" onClick={onClose} />
      </div>
      {loading ? (
        <div className="field-index-panel-loading">
          <span className="field-index-panel-spinner" />
        </div>
      ) : results.length === 0 ? (
        <div className="field-index-panel-empty">No results found</div>
      ) : (
        <div className="field-index-panel-list">
          {results.map((item, idx) => (
            <div
              key={idx}
              className="field-index-panel-item"
              onClick={() => onSelect(item)}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      open
      placement="bottomLeft"
      autoAdjustOverflow
      arrow={false}
      trigger="click"
      content={content}
      overlayClassName="field-index-popover"
      getPopupContainer={() => document.body}
    >
      <span
        className="field-index-anchor"
        style={{
          left: anchorRect.left,
          top: anchorRect.bottom,
          width: anchorRect.width,
          height: 1,
        }}
      />
    </Popover>
  );
});

/**
 * FieldIndexAutocomplete — watches the specified form fields for changes,
 * debounces, queries the field index API, and shows results near the active field.
 *
 * Props:
 *   form       — the Ant Design Form instance
 *   fieldNames — array of field names to watch (from AUTOCOMPLETE_FIELDS)
 *   userInfo   — { username } for pic filter on grouped fields
 *
 * Usage:
 *   <FieldIndexAutocomplete form={form} fieldNames={['port_of_loading', ...]} userInfo={userInfo} />
 *   Place it inside the <Form> component, after all form content.
 */
const FieldIndexAutocomplete = React.memo(function FieldIndexAutocomplete({
  form,
  fieldNames,
  userInfo,
  onFieldLoadingChange,
}) {
  // UseWatch for each field — these are the reactive triggers
  const val_port_of_loading = Form.useWatch('port_of_loading', form);
  const val_port_of_discharge = Form.useWatch('port_of_discharge', form);
  const val_place_of_delivery = Form.useWatch('place_of_delivery', form);
  const val_freight_location = Form.useWatch('freight_location', form);
  const val_freight_tel = Form.useWatch('freight_tel', form);
  const val_it_number = Form.useWatch('it_number', form);
  const val_consignee_email = Form.useWatch('consignee_email', form);
  const val_notify_email = Form.useWatch('notify_email', form);

  const allValues = useMemo(() => ({
    port_of_loading: val_port_of_loading,
    port_of_discharge: val_port_of_discharge,
    place_of_delivery: val_place_of_delivery,
    freight_location: val_freight_location,
    freight_tel: val_freight_tel,
    it_number: val_it_number,
    consignee_email: val_consignee_email,
    notify_email: val_notify_email,
  }), [
    val_port_of_loading,
    val_port_of_discharge,
    val_place_of_delivery,
    val_freight_location,
    val_freight_tel,
    val_it_number,
    val_consignee_email,
    val_notify_email,
  ]);

  const [activeFieldName, setActiveFieldName] = useState(null);
  const [pic, setPic] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const loadingFieldNameRef = useRef(null);
  const prevValuesRef = useRef({});
  const isSelectingRef = useRef(false);
  const activeInputRef = useRef(null);
  const activeInputMetaRef = useRef(null);

  const captureActiveInput = useCallback(() => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return;

    activeInputRef.current = activeElement;
    setAnchorRect(activeElement.getBoundingClientRect());
    activeInputMetaRef.current = {
      id: activeElement.id || null,
      name: activeElement.getAttribute('name') || null,
      selectionStart: typeof activeElement.selectionStart === 'number' ? activeElement.selectionStart : null,
      selectionEnd: typeof activeElement.selectionEnd === 'number' ? activeElement.selectionEnd : null,
    };
  }, []);

  const restoreInputFocus = useCallback((cursorAtEnd = false, nextValue = null) => {
    const meta = activeInputMetaRef.current;
    let target = activeInputRef.current;

    if (meta?.id) {
      target = document.getElementById(meta.id) || target;
    }

    if ((!target || !document.contains(target)) && meta?.name) {
      target = document.querySelector(`[name="${meta.name}"]`);
    }

    if (!(target instanceof HTMLElement) || typeof target.focus !== 'function') return;

    target.focus();

    if (typeof target.setSelectionRange === 'function') {
      const fallbackLength = String(nextValue ?? target.value ?? '').length;
      const start = cursorAtEnd ? fallbackLength : (meta?.selectionStart ?? fallbackLength);
      const end = cursorAtEnd ? fallbackLength : (meta?.selectionEnd ?? start);
      target.setSelectionRange(start, end);
    }

    activeInputRef.current = target;
  }, []);

  // Debounced API query
  useEffect(() => {
    const changedFieldName = fieldNames.find((name) => (
      allValues[name] !== prevValuesRef.current[name]
    ));
    prevValuesRef.current = { ...allValues };

    if (isSelectingRef.current) return; // skip query during programmatic selection
    if (!changedFieldName || !AUTOCOMPLETE_FIELDS[changedFieldName]) return;

    captureActiveInput();

    const config = AUTOCOMPLETE_FIELDS[changedFieldName];
    const value = allValues[changedFieldName];

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!value || String(value).trim().length === 0) {
      setVisible(false);
      setResults([]);
      return;
    }

    const trimmed = String(value).trim();
    const picValue = config.needPic
      ? (form.getFieldValue('pic') || userInfo?.username || null)
      : null;

    debounceTimerRef.current = setTimeout(() => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      setLoading(true);
      loadingFieldNameRef.current = config.fieldName;
      onFieldLoadingChange?.(config.fieldName, true);

      apiClient
        .get('/shipping-order/field-index/query', {
          params: {
            fieldName: config.fieldName,
            value: trimmed,
            ...(picValue ? { pic: picValue } : {}),
          },
          signal: abortControllerRef.current.signal,
        })
        .then((res) => {
          const data = res?.data?.data;
          if (data?.dataList && data.dataList.length > 0) {
            setResults(data.dataList);
            setActiveFieldName(config.fieldName);
            setPic(config.needPic ? picValue : null);
            setVisible(true);
          } else {
            setResults([]);
            setVisible(false);
          }
        })
        .catch((e) => {
          if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
            setResults([]);
            setVisible(false);
          }
        })
        .finally(() => {
          if (loadingFieldNameRef.current === config.fieldName) {
            loadingFieldNameRef.current = null;
            onFieldLoadingChange?.(config.fieldName, false);
          }
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [allValues, captureActiveInput, fieldNames, form, onFieldLoadingChange, userInfo?.username]);

  // Close panel when clicking outside
  useEffect(() => {
    const handler = (event) => {
      if (event.target instanceof HTMLElement && event.target.closest('.field-index-popover')) return;
      setVisible(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const syncAnchorRect = () => {
      const target = activeInputRef.current;
      if (target instanceof HTMLElement && document.contains(target)) {
        setAnchorRect(target.getBoundingClientRect());
      }
    };

    window.addEventListener('scroll', syncAnchorRect, true);
    window.addEventListener('resize', syncAnchorRect);
    syncAnchorRect();

    return () => {
      window.removeEventListener('scroll', syncAnchorRect, true);
      window.removeEventListener('resize', syncAnchorRect);
    };
  }, [visible]);

  // Notify parent to clear loading state on unmount
  useEffect(() => {
    return () => {
      if (loadingFieldNameRef.current) {
        onFieldLoadingChange?.(loadingFieldNameRef.current, false);
      }
    };
  }, [onFieldLoadingChange]);

  useLayoutEffect(() => {
    restoreInputFocus(false);
  }, [visible, loading, results, restoreInputFocus]);

  const handleSelect = useCallback((value) => {
    if (activeFieldName) {
      isSelectingRef.current = true;
      form.setFieldsValue({ [activeFieldName]: value });
      requestAnimationFrame(() => {
        restoreInputFocus(true, value);
      });
      setTimeout(() => { isSelectingRef.current = false; }, 100);
    }
    setVisible(false);
    setResults([]);
  }, [activeFieldName, form, restoreInputFocus]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setResults([]);
  }, []);

  return (
    <SuggestionPanel
      visible={visible}
      anchorRect={anchorRect}
      activeFieldName={activeFieldName}
      pic={pic}
      loading={loading}
      results={results}
      onSelect={handleSelect}
      onClose={handleClose}
    />
  );
});

export default FieldIndexAutocomplete;
